import React, { useEffect, useState, useRef } from 'react';
import { css } from 'twin.macro';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { motion } from 'framer-motion';
import { NavigationVariants } from '../../libs/animations';
import Button from '../Button/Button';
import TextInput from '../TextInput/TextInput';
import DateRangePicker from '../DateRangePicker/DateRangePicker';

import { printInInputDateFormat } from '../../libs/dateTimePrinting';
import { defaultDateFormat } from '../../libs/dateFormatting';
import {
  getConfiguredDateInputFormat,
  internalDateFormat,
  replaceDateSeparator,
  todayDateObject
} from '../../libs/dateTimeUtilities';
import type { DateTimeObject } from '../../libs/dateTimeTypes';
import FormatSettingsContext, { type FormatSettings } from '../FormatSettingsContext';

/**
 * The DateInput component is used to fill in a date by day, month and year in numbers.
 * Click the calendar icon to toggle the datepicker and focus on the input field if opened.
 * This component is based on the TextInput component and interacts with the same properties.
 *
 * The toggeable DateRangePicker is positioned absolute, so you'll need to provide a relative
 * wrapper to correctly position the picker. This is not provided inside this DateInput component itself,
 * because then it's width will be limited to the width of the DateInput, which is not always desired.
 */

/* The attributes handed straight to the underlying input element. onChange and
   placeholder are this component's own, so they are not among them */
type DateInputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'placeholder' | 'value' | 'defaultValue'
>;

interface DateInputProps extends DateInputAttributes {
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className?: string;
  /** Set the input to validated state */
  validated?: boolean;
  /** Fires with the raw typed text, and only when a date was typed - picking a
   * date in the datepicker does not call it. Use onDateChange for the date */
  onChange?: ((value: string) => void) | null;
  /** Fires with the date object however it was chosen: typed or picked in the
   * datepicker */
  onDateChange?: ((dateObject: DateTimeObject) => void) | null;
  initiallyShowDatePicker?: boolean;
  /** The date the field starts on. Changing it to another day from the outside
   * moves the field and the picker along with it */
  initialInputDateObject?: DateTimeObject;
  /** Min date allowed, when false default min date set inside DateRangePicker will be used */
  minDate?: DateTimeObject;
  /** Max date allowed, when false default max date set inside DateRangePicker will be used */
  maxDate?: DateTimeObject;
  datepickerShowMonthAndYearPickers?: boolean;
  placeholder?: string;
}

const DateInput = ({
  validated = false,
  initiallyShowDatePicker = false,
  initialInputDateObject = todayDateObject(),
  minDate = todayDateObject().subtract(3, 'y'),
  maxDate = todayDateObject().add(10, 'y'),
  datepickerShowMonthAndYearPickers = true,
  placeholder = printInInputDateFormat(todayDateObject(), defaultDateFormat),
  onChange = null,
  onDateChange = null,
  ...inputAttributes
}: DateInputProps) => {
  const { dateFormatSetting }: FormatSettings = React.useContext(FormatSettingsContext);
  const [showDatePicker, setShowDatePicker] = useState(initiallyShowDatePicker);
  const [inputValue, setInputValue] = useState(
    printInInputDateFormat(initialInputDateObject, dateFormatSetting)
  );
  const initialDateIsValid = moment(initialInputDateObject).isValid();
  const initialDateValueObject = initialDateIsValid
    ? moment(initialInputDateObject)
    : todayDateObject();

  const [inputDate, setInputDate] = useState(initialDateValueObject);
  const [heightDatePicker, setHeightDatePicker] = useState(0);
  const dateInputRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputFieldRef = useRef<HTMLInputElement>(null);

  const onClickOutside = () => {
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    const toggledValue = !showDatePicker;
    setShowDatePicker(toggledValue);
    if (toggledValue) {
      if (inputFieldRef) {
        if (inputFieldRef.current) {
          inputFieldRef.current.focus();
        }
      }
    }
  };

  const defineDateRange = (dateObject: DateTimeObject) => {
    return [
      {
        startDate: dateObject.toDate(),
        endDate: dateObject.toDate(),
        key: 'selection'
      }
    ];
  };

  useEffect(() => {
    if (datePickerRef) {
      if (datePickerRef.current) {
        const marginTopDatePickerEl = parseInt(
          getComputedStyle(datePickerRef.current).marginTop
        );

        setHeightDatePicker(datePickerRef.current.offsetHeight + marginTopDatePickerEl);
      } else {
        setHeightDatePicker(0);
      }
    } else {
      setHeightDatePicker(0);
    }
  }, [showDatePicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateInputRef.current && !dateInputRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [onClickOutside]);

  const initialRange = defineDateRange(initialDateValueObject);
  const [dateRange, setDateRange] = useState(initialRange);
  const [typingTimeOut, setTypingTimeOut] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  /* The field follows the caller: a date changed from outside (a shortcut
     button elsewhere on the page, say) moves the text and the picker with it.
     Keyed on the day rather than on the date object, so a caller that echoes
     back the date just chosen here does not reformat what is being typed, and
     a caller that hands a new object for the same day changes nothing */
  const initialDateKey = initialDateValueObject.format(internalDateFormat);
  useEffect(() => {
    if (!initialDateIsValid) return;
    if (initialDateKey === inputDate.format(internalDateFormat)) return;
    setInputDate(initialDateValueObject);
    setInputValue(printInInputDateFormat(initialDateValueObject, dateFormatSetting));
    setDateRange(defineDateRange(initialDateValueObject));
  }, [initialDateKey, initialDateIsValid]);

  const onChangeDateRange = (range: Array<{ startDate: Date }>) => {
    const selectedDayDateObject = moment(range[0].startDate);
    const updatedDateRange = defineDateRange(selectedDayDateObject);
    setDateRange(updatedDateRange);
    setInputValue(printInInputDateFormat(selectedDayDateObject, dateFormatSetting));
    setInputDate(selectedDayDateObject);
    setShowDatePicker(false);
    if (onDateChange) onDateChange(selectedDayDateObject);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target;
    const reformatValueIfNeeded = replaceDateSeparator(value, dateFormatSetting);
    /* Instantly giving type feedback */
    setInputValue(reformatValueIfNeeded);

    const currentTimeOut = typingTimeOut;
    if (currentTimeOut) clearTimeout(currentTimeOut);
    setTypingTimeOut(
      setTimeout(() => {
        const confInputDateFormat = getConfiguredDateInputFormat(dateFormatSetting);
        const selectedDayDateObject = moment(reformatValueIfNeeded, confInputDateFormat);
        if (selectedDayDateObject.isValid()) {
          setInputDate(selectedDayDateObject);
          const updatedDateRange = defineDateRange(selectedDayDateObject);
          setDateRange(updatedDateRange);
          if (onChange) onChange(value);
          if (onDateChange) onDateChange(selectedDayDateObject);
        }
      }, 500)
    );
  };

  const onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (!showDatePicker) {
      setShowDatePicker(true);
    }
  };

  return (
    <div
      ref={dateInputRef}
      css={css`
        padding-bottom: ${heightDatePicker}px;
      `}
    >
      <div>
        <TextInput
          {...inputAttributes}
          inputValue={inputValue}
          placeholder={placeholder}
          rightElement={
            <Button
              contentType="icon"
              icon="calendar_month"
              size="small"
              onClick={toggleDatePicker}
              type="button"
            />
          }
          onChange={onInputChange}
          onFocus={onInputFocus}
          validated={validated}
          ref={inputFieldRef}
        />
      </div>
      {showDatePicker && (
        <div tw="w-full my-8 absolute text-center left-0 bg-white" ref={datePickerRef}>
          <div tw="bg-white inline-block md:px-16">
            {/* framer-motion 6 predates React 18 dropping implicit children
                from component props, so motion.div's props type rejects them
                under the React 19 types. It goes away with the upgrade */}
            {/* @ts-expect-error see above */}
            <motion.div
              initial="toggleFromTopInvisible"
              animate="toggleFromTopVisible"
              exit="toggleFromTopInvisible"
              variants={NavigationVariants}
            >
              <DateRangePicker
                dateRange={dateRange}
                onChange={onChangeDateRange}
                minDate={minDate}
                maxDate={maxDate}
                showMonthAndYearPickers={datepickerShowMonthAndYearPickers}
                showMonthArrow={datepickerShowMonthAndYearPickers}
              />
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateInput;
